import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { DataTable, FilterDialog } from "../../components";
import { deletePlayer, getPlayerList, getPlayerNationalities, banPlayer, unbanPlayer } from "../../api/players";
import { IPlayer } from "../../models/IPlayer";
import { exportDataToExcel } from "../../utils/excel";
import { BrandGoogleSolid, CheckOctagon, CloudDownload, DangerOctagon, Envelope } from "@mynaui/icons-react";
import moment from "moment";
import { getLanguageList } from "../../api/general";
import { Filter, FilterButton } from "../../components/FilterDialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import lifeLogo from "../../assets/images/life-logo.png";
import { assignRole } from "../../api/auth";
import BanPlayerDialog from "../../components/BanPlayerDialog";
import "./styles.scss";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useDebouncedSearch } from "../../hooks";
import { Button, Loader, PageHeader, Tag } from "../../ui";

const Players = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState<IPlayer[]>([]);

  const [banTarget, setBanTarget] = useState<IPlayer | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sortColumn, setSortColumn] = useState<"firstname" | "createdat" | "nationality" | "tshirtsize" | "gender">(
    "createdat"
  );

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<Filter[]>([]);

  // Ref to track current fetch request and prevent duplicates
  const currentFetchRef = useRef<string>("");
  const isFetchingRef = useRef(false);

  const parseFiltersFromParams = useCallback(() => {
    const params = Object.fromEntries(searchParams.entries());
    const gender = params.gender?.split(",") || [];
    const nationality = params.nationality?.split(",") || [];
    const tshirtSize = params.tshirtSize?.split(",") || [];
    const languages = params.languages?.split(",") || [];
    const verified = params.verified || "";
    const banned = params.banned || "";
    const lifeMember = params.lifeMember || "";
    const playaResident = params.playaResident || "";

    return { gender, nationality, tshirtSize, languages, verified, banned, lifeMember, playaResident };
  }, [searchParams]);

  const getLanguages = async () => {
    try {
      const res = await getLanguageList();
      return res.data;
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getNationalities = async () => {
    try {
      const res = await getPlayerNationalities();
      return res.data;
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleSortChange = useCallback(
    (columnKey: "firstname" | "createdat" | "nationality" | "tshirtsize" | "gender", direction: "asc" | "desc") => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("sortColumn", columnKey);
        newParams.set("sortDirection", direction);
        newParams.delete("page");
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (newPage > 1) {
          newParams.set("page", newPage.toString());
        } else {
          newParams.delete("page");
        }

        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleFilterApply = (newFilters: Filter[]) => {
    setFilters(newFilters);

    const filtersApplied = newFilters.filter((x) => x.options?.some((o) => o.selected));

    let genderFilters: string[] = [];
    let nationalityFilters: string[] = [];
    let tshirtSizeFilters: string[] = [];
    let languageFilters: string[] = [];
    let verifiedFilter: string = "";
    let bannedFilter: string = "";
    let lifeMemberFilter: string = "";
    let playaResidentFilter: string = "";

    filtersApplied.forEach((filter) => {
      const value = filter.options ? filter.options.filter((x) => x.selected).map((x) => x.value) : [];

      if (filter.label === "Gender") genderFilters = value;
      if (filter.label === "Nationality") nationalityFilters = value;
      if (filter.label === "Tshirt Size") tshirtSizeFilters = value;
      if (filter.label === "Languages") languageFilters = value;
      if (filter.label === "Email Verified") {
        const value = filter.options ? filter.options.find((x) => x.selected)?.value : "";
        if (value === "true") verifiedFilter = "true";
        if (value === "false") verifiedFilter = "false";
      }
      if (filter.label === "Banned") {
        const value = filter.options ? filter.options.find((x) => x.selected)?.value : "";
        if (value === "true") bannedFilter = "true";
        if (value === "false") bannedFilter = "false";
      }
      if (filter.label === "Life Member") {
        const value = filter.options ? filter.options.find((x) => x.selected)?.value : "";
        if (value === "true") lifeMemberFilter = "true";
        if (value === "false") lifeMemberFilter = "false";
      }
      if (filter.label === "Playa Resident") {
        const value = filter.options ? filter.options.find((x) => x.selected)?.value : "";
        if (value === "true") playaResidentFilter = "true";
        if (value === "false") playaResidentFilter = "false";
      }
    });

    const newParams = new URLSearchParams();

    if (genderFilters.length > 0) newParams.set("gender", genderFilters.join(","));
    if (nationalityFilters.length > 0) newParams.set("nationality", nationalityFilters.join(","));
    if (tshirtSizeFilters.length > 0) newParams.set("tshirtSize", tshirtSizeFilters.join(","));
    if (languageFilters.length > 0) newParams.set("languages", languageFilters.join(","));
    if (verifiedFilter) newParams.set("verified", verifiedFilter);
    if (bannedFilter) newParams.set("banned", bannedFilter);
    if (lifeMemberFilter) newParams.set("lifeMember", lifeMemberFilter);
    if (playaResidentFilter) newParams.set("playaResident", playaResidentFilter);
    setSearchParams(newParams);
  };

  const handleViewPlayer = (player: IPlayer) => {
    navigate(`${player.id}`);
  };

  const handleSearch = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const prevSearch = prev.get("search") || "";

        // Only update if search value actually changed
        if (prevSearch === value) {
          return prev;
        }

        const newParams = new URLSearchParams(prev);
        if (value) {
          newParams.set("search", value);
        } else {
          newParams.delete("search");
        }
        newParams.delete("page"); // Reset to first page when searching
        return newParams;
      });
    },
    [setSearchParams]
  );

  const { searchValue, setSearchValue } = useDebouncedSearch(handleSearch, 500, searchParams.get("search") || "");

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { gender, languages, nationality, tshirtSize, verified } = parseFiltersFromParams();
      const res = await getPlayerList(
        1,
        1000,
        "",
        sortColumn,
        sortDirection,
        gender,
        nationality,
        tshirtSize,
        languages,
        verified
      );
      exportDataToExcel(
        res.data.items.map((player) => ({
          name: `${player.firstName} ${player.lastName ?? ""}`,
          email: player.email,
          ["Account Type"]: player.accountType,
          gender: player.gender,
          ["Email Verified"]: player.emailVerified,
          nationality: player.nationality,
          birthDate: player.birthDate,
          tshirtSize: player.tshirtSize,
          phoneNumber: player.phoneNumber,
          languages: player.languages.map((x) => x.name).join(", "),
          instagram: player.instagramAccount,
          ["Life Member"]: player.isLifeMember ? "Yes" : "No",
          ["Life Member ID"]: player.lifeMembershipId || "",
          ["Date Registered"]: player.createdAt,
        })),
        "players"
      );
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleRoleSwitch = async (player: IPlayer) => {
    setLoading(true);
    try {
      await assignRole({ email: player.email, role: "coach" });
      // Trigger refetch by updating a URL param temporarily
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("_refetch", Date.now().toString());
        return newParams;
      });
    } catch (error) {
      console.error("error switching role");
    } finally {
      dispatch(
        setGeneralAlertMessage({
          message: `${player.firstName} ${player.lastName ?? ""} is now a representer`,
          type: "success",
        })
      );
    }
  };

  const handleDelete = async (item: IPlayer) => {
    setLoading(true);
    try {
      await deletePlayer(item.id);
      // Trigger refetch by updating a URL param temporarily
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("_refetch", Date.now().toString());
        return newParams;
      });
    } catch (error: any) {
      console.error("player deletion failed", error.message);
    }
  };

  const getData = async () => {
    const languages = await getLanguages();
    const nationalities = await getNationalities();
    const { gender, nationality, tshirtSize, languages: langParams, verified, banned, lifeMember, playaResident } = parseFiltersFromParams();

    const newFilters: Filter[] = [
      {
        label: "Gender",
        options: [
          { label: "Male", value: "Male", selected: gender.includes("Male") },
          { label: "Female", value: "Female", selected: gender.includes("Female") },
        ],
      },
      {
        label: "Nationality",
        options: nationalities?.map((item: string) => ({
          label: item,
          value: item,
          selected: nationality.includes(item),
        })),
      },
      {
        label: "Email Verified",
        radio: true,
        options: [
          { label: "Verified", value: "true", selected: verified === "true" },
          { label: "Unverified", value: "false", selected: verified === "false" },
        ],
      },
      {
        label: "Tshirt Size",
        options: [
          { label: "S", value: "S", selected: tshirtSize.includes("S") },
          { label: "M", value: "M", selected: tshirtSize.includes("M") },
          { label: "L", value: "L", selected: tshirtSize.includes("L") },
          { label: "XL", value: "XL", selected: tshirtSize.includes("XL") },
        ],
      },
      {
        label: "Languages",
        showMore: false,
        options: languages.map((item: { name: string; id: string }) => ({
          label: item.name,
          value: item.name,
          selected: langParams.includes(item.name),
        })),
      },
      {
        label: "Banned",
        radio: true,
        options: [
          { label: "Banned", value: "true", selected: banned === "true" },
          { label: "Not Banned", value: "false", selected: banned === "false" },
        ],
      },
      {
        label: "Life Member",
        radio: true,
        options: [
          { label: "Life Member", value: "true", selected: lifeMember === "true" },
          { label: "Non-Member", value: "false", selected: lifeMember === "false" },
        ],
      },
      {
        label: "Playa Resident",
        radio: true,
        options: [
          { label: "Playa Resident", value: "true", selected: playaResident === "true" },
          { label: "Non-Resident", value: "false", selected: playaResident === "false" },
        ],
      },
    ];

    setFilters(newFilters);
  };

  useEffect(() => {
    document.title = "Players";
    getData();
  }, []);

  // SINGLE useEffect - handles ALL data fetching
  useEffect(() => {
    const fetchData = async () => {
      // Create a unique key for this fetch request
      const fetchKey = searchParams.toString();

      // If we're already fetching this exact request, skip
      if (isFetchingRef.current && currentFetchRef.current === fetchKey) {
        return;
      }

      // Mark as fetching
      isFetchingRef.current = true;
      currentFetchRef.current = fetchKey;

      // Parse params
      const page = searchParams.get("page");
      const sortCol = searchParams.get("sortColumn");
      const sortDir = searchParams.get("sortDirection");
      const search = searchParams.get("search") || "";
      const gender = searchParams.get("gender") ? searchParams.get("gender")!.split(",") : [];
      const nationality = searchParams.get("nationality") ? searchParams.get("nationality")!.split(",") : [];
      const tshirtSize = searchParams.get("tshirtSize") ? searchParams.get("tshirtSize")!.split(",") : [];
      const languages = searchParams.get("languages") ? searchParams.get("languages")!.split(",") : [];
      const verified = searchParams.get("verified") || "";
      const banned = searchParams.get("banned") || "";
      const lifeMember = searchParams.get("lifeMember") || "";
      const playaResident = searchParams.get("playaResident") || "";

      const pageNum = page ? parseInt(page) : 1;
      const sortColumn =
        sortCol && ["firstName", "createdAt", "nationality", "tshirtSize", "gender"].includes(sortCol)
          ? (sortCol as any)
          : "createdAt";
      const sortDirection = sortDir && ["asc", "desc"].includes(sortDir) ? (sortDir as any) : "desc";

      // Update local state
      setPageNumber(pageNum);
      setSortColumn(sortColumn);
      setSortDirection(sortDirection);
      setLoading(true);

      try {
        const { data } = await getPlayerList(
          pageNum,
          pageSize,
          search,
          sortColumn,
          sortDirection,
          gender,
          nationality,
          tshirtSize,
          languages,
          verified,
          banned,
          lifeMember,
          playaResident
        );

        // Only update if this is still the current request
        if (currentFetchRef.current === fetchKey) {
          setPlayers(data.items);
          setTotalPages(data.pagination.totalPages);
          setTotalItems(data.pagination.total);
        }
      } catch (err: any) {
        if (err.message === "No players found") setPlayers([]);
        console.error("Error fetching players", err);
      } finally {
        // Only set loading false if this is still the current request
        if (currentFetchRef.current === fetchKey) {
          setLoading(false);
          isFetchingRef.current = false;
        }
      }
    };

    fetchData();
  }, [searchParams, pageSize]);

  const filtersApplied = useMemo(() => filters.filter((x) => x.options?.some((o) => o.selected)).length, [filters]);

  return (
    <div className="players-page">
      <PageHeader
        title="Players"
        count={totalItems}
        actions={
          <>
            <FilterButton count={filtersApplied} onClick={() => setIsFilterDialogOpen(true)} />
            <Button
              icon={<CloudDownload size={18} />}
              loading={exportLoading}
              onClick={handleExport}
            >
              Export
            </Button>
          </>
        }
      />

      <FilterDialog
        isOpen={isFilterDialogOpen}
        onApply={handleFilterApply}
        onClose={() => setIsFilterDialogOpen(false)}
        filters={filters}
      />

      {loading && players.length === 0 ? (
        <Loader />
      ) : (
        <DataTable
          id={"players-table"}
          data={players}
          editable
          label={"players"}
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onViewItem={handleViewPlayer}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onSwitchRole={handleRoleSwitch}
          canDelete
          onDelete={handleDelete}
          canBan
          onBan={(item: IPlayer) => setBanTarget(item)}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          loading={loading}
          columns={[
            {
              key: "firstName",
              label: "Name",
              render: (_: any, row: IPlayer) => (
                <span className="people-name">
                  <span>
                    {`${row.firstName.charAt(0).toUpperCase()}${row.firstName.slice(1)} ${
                      row.lastName ? `${row.lastName.at(0)?.toUpperCase()}${row.lastName.slice(1)}` : ""
                    }`}
                  </span>
                  {row.isBanned && <Tag tone="danger">Banned</Tag>}
                  {row.isLifeMember && <img src={lifeLogo} alt="Life" className="people-life-logo" />}
                  {row.isPlayaResident && <Tag tone="yellow">Playa</Tag>}
                </span>
              ),
              sortable: true,
            },
            {
              key: "accountType",
              label: "",
              render: (_: any, row: IPlayer) =>
                row.accountType === "email" ? (
                  <Envelope className="people-account-icon" size={16} />
                ) : (
                  <BrandGoogleSolid className="people-account-icon" size={16} />
                ),
            },
            { key: "email", label: "Email" },
            { key: "gender", label: "Gender", sortable: true },
            {
              key: "emailVerified",
              label: "Verified",
              center: true,
              render: (_: any, row: IPlayer) =>
                row.emailVerified ? (
                  <CheckOctagon size={20} className="people-verified" />
                ) : (
                  <DangerOctagon size={20} className="people-unverified" />
                ),
            },
            { key: "nationality", label: "Nationality", sortable: true },
            {
              key: "birthDate",
              label: "Birthdate",
              uppercase: true,
              render: (_: any, row: IPlayer) => (row.birthDate ? moment(row.birthDate).format("DD MMM YYYY") : "-"),
            },
            { key: "tshirtSize", label: "T-shirt Size", sortable: true, center: true },
            {
              key: "phoneNumber",
              label: "Phone Number",
              render: (_: any, row: IPlayer) => row.phoneNumber ?? "-",
            },
            {
              key: "languages",
              label: "Languages",
              render: (_: any, row: IPlayer) =>
                row.languages.length > 0 ? row.languages.map((x) => x.name).join(", ") : "-",
            },
            {
              key: "instagramAccount",
              label: "Instagram",
              render: (value: any) =>
                value ? (
                  <a href={`https://instagram.com/${value.replace("@", "")}`} target="_blank" rel="noreferrer">
                    @{value.replace("@", "")}
                  </a>
                ) : (
                  "-"
                ),
            },
            {
              key: "lifeMembershipId",
              label: "Life Member ID",
              render: (_: any, row: IPlayer) =>
                row.isLifeMember ? (
                  <span className="people-life-id">{row.lifeMembershipId || "Yes"}</span>
                ) : (
                  "-"
                ),
            },
            {
              key: "createdAt",
              label: "Date Registered",
              render: (_: any, row: IPlayer) => moment(row.createdAt).format("DD MMM, YYYY"),
              sortable: true,
            },
          ]}
        />
      )}
      <BanPlayerDialog
        isOpen={!!banTarget}
        isBanned={banTarget?.isBanned ?? false}
        playerName={banTarget ? `${banTarget.firstName} ${banTarget.lastName ?? ""}`.trim() : ""}
        onBan={async (reason) => {
          if (!banTarget) return;
          await banPlayer(banTarget.id, reason);
          dispatch(setGeneralAlertMessage({ type: "success", message: "Player banned successfully" }));
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("_refetch", Date.now().toString());
            return newParams;
          });
        }}
        onUnban={async () => {
          if (!banTarget) return;
          await unbanPlayer(banTarget.id);
          dispatch(setGeneralAlertMessage({ type: "success", message: "Player unbanned successfully" }));
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("_refetch", Date.now().toString());
            return newParams;
          });
        }}
        onClose={() => setBanTarget(null)}
      />
    </div>
  );
};

export default Players;
